import {editorValueSignature} from './native-envelope';
import {readCompanySpeechSource} from './speech-source';
import {companyVoiceOwner} from './voice-owner';
import type {CompanySpeechClip} from './speech-clip';

export type CompanyVoiceClip = CompanySpeechClip & {
  kind: 'audio';
  filename: string;
  sourceUrl: string;
  sourceStartInSeconds: number;
  sourceEndInSeconds: number;
  playbackRate: number;
  partialSource: boolean;
  /** Immediate parent first. Present only for a native group member. */
  groupPath?: string[];
};
export type VoiceReplacementPolicy = 'natural' | 'fit' | 'ripple-track';
export const isCompanyVoiceClip = (source: CompanySpeechClip): source is CompanyVoiceClip => 'kind' in source && source.kind === 'audio';
const record = (value: unknown): value is Record<string, unknown> => !!value && typeof value === 'object' && !Array.isArray(value);
const finite = (value: unknown): value is number => typeof value === 'number' && Number.isFinite(value);
const frame = (value: unknown): value is number => finite(value) && Number.isSafeInteger(value) && value >= 0;

export function selectedCompanyVoiceClip(document: unknown, selected: readonly string[], inspectedMemberId?: string | null): string | null {
  if (!record(document) || !record(document.items)) return null;
  const items = document.items;
  const voices = selected.filter(id => record(items[id]) && items[id].type === 'audio');
  if (voices.length === 1) return voices[0]!;
  if (voices.length || selected.length !== 1 || !inspectedMemberId || !record(items[inspectedMemberId])
    || items[inspectedMemberId].type !== 'audio') return null;
  try { return companyVoiceOwner(document, inspectedMemberId).parentIds.includes(selected[0]!) ? inspectedMemberId : null; }
  catch { return null; }
}

/** Source facts and a proposal only: a trimmed recording is not automatically a verbatim transcript. */
export function readCompanyVoiceClip(document: unknown, id: string): CompanyVoiceClip {
  if (!record(document) || !record(document.items) || !record(document.assets) || !Array.isArray(document.tracks)
    || !finite(document.fps) || document.fps <= 0) throw new Error('음성 편집 자료를 확인해 주세요.');
  const item = document.items[id];
  if (!record(item) || item.id !== id || item.type !== 'audio' || typeof item.assetId !== 'string'
    || !frame(item.from) || !frame(item.durationInFrames) || item.durationInFrames < 1
    || !finite(item.audioStartFromInSeconds) || item.audioStartFromInSeconds < 0 || !finite(item.playbackRate) || item.playbackRate <= 0) {
    throw new Error('교체할 오디오 클립 하나를 선택해 주세요.');
  }
  const ownership = companyVoiceOwner(document, id), track = ownership.track;
  if (track.locked) throw new Error('음성이 들어 있는 트랙의 잠금을 풀고 다시 선택해 주세요.');
  for (const parent of ownership.parents) {
    if (parent.preserveGroupFrame !== true) continue;
    if (!frame(parent.from) || !frame(parent.durationInFrames) || parent.durationInFrames < 1
      || !Number.isSafeInteger(parent.from + parent.durationInFrames)) throw new Error('부모 그룹의 시간 구간을 확인해 주세요.');
    if (item.from < parent.from || item.from + item.durationInFrames > parent.from + parent.durationInFrames)
      throw new Error('부모 그룹에서 일부가 잘린 음성입니다. 음성 생성·교체 메뉴의 그룹 구간에 맞춰 자르기를 사용하거나 부모의 자르기를 조정해 주세요.');
  }
  const asset = document.assets[item.assetId];
  if (!record(asset) || asset.id !== item.assetId || asset.type !== 'audio' || typeof asset.remoteUrl !== 'string'
    || !finite(asset.durationInSeconds) || asset.durationInSeconds <= 0) throw new Error('원본 음성 파일을 확인해 주세요.');
  const url = new URL(asset.remoteUrl);
  if (!['https:', 'http:'].includes(url.protocol) || url.username || url.password) throw new Error('원본 음성 주소를 확인해 주세요.');
  const end = item.audioStartFromInSeconds + item.durationInFrames / document.fps * item.playbackRate;
  if (!finite(end) || end > asset.durationInSeconds + item.playbackRate / document.fps) throw new Error('원본 음성 범위를 벗어난 클립입니다. 시작과 길이를 확인해 주세요.');
  const origin = readCompanySpeechSource(item.speechSource, asset.remoteUrl);
  const generated = record(asset.wakaSpeech) && asset.wakaSpeech.sourceUrl === asset.remoteUrl ? asset.wakaSpeech : null;
  const text = origin?.text ?? (generated && typeof generated.text === 'string' ? generated.text : '');
  const originalDuration = origin?.durationSec ?? asset.durationInSeconds;
  const result = {
    kind: 'audio' as const, clipId: id, assetId: item.assetId, trackId: ownership.trackId,
    filename: typeof asset.filename === 'string' ? asset.filename : '오디오', text,
    from: item.from, durationInFrames: item.durationInFrames, fps: document.fps,
    sourceUrl: asset.remoteUrl, sourceStartInSeconds: item.audioStartFromInSeconds,
    sourceEndInSeconds: end, playbackRate: item.playbackRate,
    ...(ownership.parentIds.length ? {groupPath: ownership.parentIds} : {}),
    partialSource: item.audioStartFromInSeconds > .000001 || Math.abs(end - originalDuration) > item.playbackRate / document.fps,
  };
  return {...result, signature: editorValueSignature({item, asset, fps: document.fps, trackId: ownership.trackId,
    ...(ownership.parents.length ? {groups: ownership.parents} : {})})};
}
